import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { DashboardPage } from "../pages/DashboardPage";
import { DeskPage } from "../pages/DeskPage";
import { InsightsPage } from "../pages/InsightsPage";
import { MoneyPage } from "../pages/MoneyPage";
import { PartiesPage } from "../pages/PartiesPage";
import { BrandMark, BrandWordmark } from "../shared/BrandIdentity";
import { BackupShareDialog } from "../shared/BackupShareDialog";
import { exportPartyStatementToCsvFile } from "../trips/csv";
import {
  canShareWorkbookFile,
  createTripWorkbookFile,
  downloadPreparedTripWorkbookFile,
  exportTripWorkbookFile,
  importTripWorkbookFile,
  sharePreparedTripWorkbookFile,
  type WorkbookFileResult,
} from "../trips/workbook";
import { TripFormDrawer } from "../trips/TripFormDrawer";
import { TripRegisterPage } from "../trips/TripRegisterPage";
import { AppShell, type AppTheme, type AppView, type DataMessage } from "./AppShell";
import {
  DEFAULT_TRIP_FILTERS,
  type DashboardPeriod,
  filterTrips,
  getActiveTrips,
  getContributionRanking,
  getDashboardMetrics,
  getDashboardStatusTrend,
  getExceptionQueue,
  getInsightDeltas,
  getInsightData,
  getInsightQualitySignals,
  getDefaultInsightYear,
  getInsightMonthOptions,
  getInsightYearOptions,
  getOperationalQueues,
  getPartyOutstanding,
  getPriorInsightData,
  getRisksByTrip,
  getRouteCostVariance,
  getTripFieldSuggestions,
  getTripsForDashboardPeriod,
  getVehiclePerformance,
  type TripFilters,
} from "../trips/selectors";
import type {
  BackupMeta,
  DataProtectionState,
  ExpenseRecord,
  InsightPeriodMode,
  PaymentRecord,
  Trip,
  TripFormData,
  TripSavePayload,
  TripStatus,
} from "../trips/types";
import { hydrateTrip } from "../trips/rules";
import { repeatTripToForm } from "../trips/tripForm.model";
import {
  deleteTripById,
  getDataProtectionState,
  initializeTripStorage,
  loadBackupMeta,
  loadTripRecords,
  loadTrips,
  requestPersistentStorage,
  saveTrip,
  saveTripWithRecords,
} from "../trips/storage";

export default function TransportERP() {
  const importInputRef = useRef<HTMLInputElement>(null);
  const pendingDeleteTimers = useRef<Map<string, number>>(new Map());
  const [trips, setTrips] = useState<Trip[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const [activeView, setActiveView] = useState<AppView>("dashboard");
  const [searchQuery, setSearchQuery] = useState("");
  const [tripFilters, setTripFilters] =
    useState<TripFilters>(DEFAULT_TRIP_FILTERS);
  const [theme, setTheme] = useState<AppTheme>(() =>
    window.localStorage.getItem("haulledger_theme") === "light" ? "light" : "dark"
  );
  const [controlPeriod, setControlPeriod] = useState<DashboardPeriod>("today");
  const [isInsightAllTime, setIsInsightAllTime] = useState(false);
  const [selectedInsightYear, setSelectedInsightYear] = useState("");
  const [selectedInsightMonth, setSelectedInsightMonth] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [editingPayments, setEditingPayments] = useState<PaymentRecord[]>([]);
  const [editingExpenses, setEditingExpenses] = useState<ExpenseRecord[]>([]);
  const [initialForm, setInitialForm] = useState<TripFormData | null>(null);
  const [dataProtectionState, setDataProtectionState] =
    useState<DataProtectionState>("unsupported");
  const [backupMeta, setBackupMeta] = useState<BackupMeta>({});
  const [preparedShare, setPreparedShare] = useState<WorkbookFileResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataBusy, setIsDataBusy] = useState(false);
  const [dataMessage, setDataMessage] = useState<DataMessage>({
    tone: "info",
    text: "Saved on this device",
  });

  useEffect(() => {
    let isMounted = true;

    initializeTripStorage()
      .then(async ({ trips: storedTrips, migratedCount }) => {
        if (!isMounted) return;
        const [protectionState, storedBackupMeta] = await Promise.all([
          getDataProtectionState(),
          loadBackupMeta(),
        ]);
        if (!isMounted) return;
        setTrips(storedTrips);
        setDataProtectionState(protectionState);
        setBackupMeta(storedBackupMeta);
        setDataMessage({
          tone: migratedCount > 0 ? "success" : "info",
          text:
            migratedCount > 0
              ? `Moved ${migratedCount} trips to local database`
              : "Saved on this device",
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setDataMessage({
          tone: "error",
          text: "Local database could not open",
        });
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem("haulledger_theme", theme);
  }, [theme]);

  useEffect(
    () => () => {
      pendingDeleteTimers.current.forEach((timerId) => window.clearTimeout(timerId));
      pendingDeleteTimers.current.clear();
    },
    []
  );

  const refreshTrips = async () => {
    setTrips(await loadTrips());
  };

  const refreshBackupMeta = async () => {
    setBackupMeta(await loadBackupMeta());
  };

  const runDataAction = async (
    action: () => Promise<void>,
    successText: string
  ) => {
    setIsDataBusy(true);
    try {
      await action();
      await Promise.all([refreshTrips(), refreshBackupMeta()]);
      setDataMessage({ tone: "success", text: successText });
    } catch {
      setDataMessage({ tone: "error", text: "Local data action failed" });
    } finally {
      setIsDataBusy(false);
    }
  };

  const visibleTrips = useMemo(
    () => trips.filter((trip) => !pendingDeleteIds.has(trip.id)),
    [pendingDeleteIds, trips]
  );
  const filteredTrips = useMemo(
    () => filterTrips(visibleTrips, searchQuery, tripFilters),
    [visibleTrips, searchQuery, tripFilters]
  );
  const risksByTrip = useMemo(() => getRisksByTrip(visibleTrips), [visibleTrips]);
  const metrics = useMemo(
    () => getDashboardMetrics(visibleTrips, risksByTrip),
    [visibleTrips, risksByTrip]
  );
  const periodTrips = useMemo(
    () => getTripsForDashboardPeriod(visibleTrips, controlPeriod),
    [visibleTrips, controlPeriod]
  );
  const periodMetrics = useMemo(
    () => getDashboardMetrics(periodTrips, risksByTrip),
    [periodTrips, risksByTrip]
  );
  const activeTrips = useMemo(() => getActiveTrips(visibleTrips), [visibleTrips]);
  const risks = useMemo(
    () => getExceptionQueue(visibleTrips, risksByTrip),
    [visibleTrips, risksByTrip]
  );
  const operationalQueues = useMemo(
    () => getOperationalQueues(visibleTrips, risksByTrip),
    [visibleTrips, risksByTrip]
  );
  const dashboardTrend = useMemo(
    () => getDashboardStatusTrend(visibleTrips, risksByTrip),
    [visibleTrips, risksByTrip]
  );
  const insightYearOptions = useMemo(
    () => getInsightYearOptions(visibleTrips),
    [visibleTrips]
  );
  const insightMonthsByYear = useMemo(
    () =>
      Object.fromEntries(
        insightYearOptions.map((year) => [
          year,
          getInsightMonthOptions(visibleTrips, year),
        ])
      ),
    [insightYearOptions, visibleTrips]
  );
  const insightMonthOptions = useMemo(
    () => getInsightMonthOptions(visibleTrips, selectedInsightYear),
    [visibleTrips, selectedInsightYear]
  );
  const partyOutstanding = useMemo(
    () => getPartyOutstanding(visibleTrips),
    [visibleTrips]
  );
  const tripSuggestions = useMemo(
    () => getTripFieldSuggestions(visibleTrips),
    [visibleTrips]
  );
  const tripsById = useMemo(
    () => new Map(visibleTrips.map((trip) => [trip.id, trip])),
    [visibleTrips]
  );

  useEffect(() => {
    if (isLoading) return;
    if (
      !selectedInsightYear ||
      !insightYearOptions.includes(selectedInsightYear)
    ) {
      setSelectedInsightYear(getDefaultInsightYear(visibleTrips));
    }
  }, [isLoading, insightYearOptions, selectedInsightYear, visibleTrips]);

  useEffect(() => {
    if (
      selectedInsightMonth &&
      !insightMonthOptions.includes(selectedInsightMonth)
    ) {
      setSelectedInsightMonth("");
    }
  }, [insightMonthOptions, selectedInsightMonth]);

  const openNewTrip = () => {
    setEditingTrip(null);
    setEditingPayments([]);
    setEditingExpenses([]);
    setInitialForm(null);
    setIsFormOpen(true);
  };

  const openEditTrip = async (trip: Trip) => {
    setEditingTrip(trip);
    setInitialForm(null);
    setEditingPayments([]);
    setEditingExpenses([]);
    setIsFormOpen(true);
    const records = await loadTripRecords(trip.id);
    setEditingPayments(records.payments);
    setEditingExpenses(records.expenses);
  };

  const openRepeatTrip = (trip: Trip) => {
    setEditingTrip(null);
    setEditingPayments([]);
    setEditingExpenses([]);
    setInitialForm(repeatTripToForm(trip));
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingTrip(null);
    setEditingPayments([]);
    setEditingExpenses([]);
    setInitialForm(null);
  };

  const upsertTrip = async (payload: TripSavePayload) => {
    await runDataAction(async () => {
      await saveTripWithRecords(payload);
      closeForm();
    }, "Trip saved locally");
  };

  const undoDelete = (tripId: string) => {
    const timerId = pendingDeleteTimers.current.get(tripId);
    if (timerId) window.clearTimeout(timerId);
    pendingDeleteTimers.current.delete(tripId);
    setPendingDeleteIds((current) => {
      const next = new Set(current);
      next.delete(tripId);
      return next;
    });
    setDataMessage({ tone: "success", text: "Trip restored" });
  };

  const deleteTrip = (tripId: string) => {
    const trip = trips.find((item) => item.id === tripId);
    if (!trip) return;
    if (
      !window.confirm(
        `Remove ${trip.tripNo || "this trip"}? You can undo for 10 seconds.`
      )
    ) {
      return;
    }

    setPendingDeleteIds((current) => new Set(current).add(tripId));
    const timerId = window.setTimeout(async () => {
      pendingDeleteTimers.current.delete(tripId);
      try {
        await deleteTripById(tripId);
        await Promise.all([refreshTrips(), refreshBackupMeta()]);
      } catch {
        undoDelete(tripId);
        setDataMessage({ tone: "error", text: "Trip could not be deleted" });
      }
    }, 10_000);
    pendingDeleteTimers.current.set(tripId, timerId);
    setDataMessage({
      tone: "info",
      text: `${trip.tripNo || "Trip"} removed`,
      durationMs: 10_000,
      action: { label: "Undo", onClick: () => undoDelete(tripId) },
    });
  };

  const updateStatus = async (tripId: string, status: TripStatus) => {
    const trip = trips.find((item) => item.id === tripId);
    if (!trip) return;

    const updatedTrip = hydrateTrip({
      ...trip,
      status,
      settledAt:
        status === "Settled" ? trip.settledAt ?? new Date().toISOString() : undefined,
      updatedAt: new Date().toISOString(),
    });

    await runDataAction(async () => {
      await saveTrip(updatedTrip);
    }, "Trip status saved locally");
  };

  const exportWorkbook = async () => {
    setIsDataBusy(true);
    try {
      const exportedCount = await exportTripWorkbookFile();
      await refreshBackupMeta();
      setDataMessage({
        tone: "success",
        text:
          exportedCount > 0
            ? `Exported ${exportedCount} data rows`
            : "Downloaded data format",
      });
    } catch {
      setDataMessage({ tone: "error", text: "Data export failed" });
    } finally {
      setIsDataBusy(false);
    }
  };

  const prepareWorkbookShare = async () => {
    setIsDataBusy(true);
    try {
      const result = await createTripWorkbookFile();
      setPreparedShare(result);
      setDataMessage({
        tone: "info",
        text: "Backup file is ready to share",
      });
    } catch {
      setDataMessage({ tone: "error", text: "Backup file could not be prepared" });
    } finally {
      setIsDataBusy(false);
    }
  };

  const sharePreparedWorkbook = () => {
    const currentShare = preparedShare;
    if (!currentShare) return;

    setIsDataBusy(true);
    const shareResult = sharePreparedTripWorkbookFile(currentShare.file);

    void shareResult
      .then(async () => {
        await refreshBackupMeta();
        setDataMessage({
          tone: "success",
          text: `Shared ${currentShare.recordCount} data rows`,
        });
        setPreparedShare(null);
      })
      .catch(async (error) => {
        const wasCancelled =
          error instanceof DOMException && error.name === "AbortError";

        if (wasCancelled) {
          setDataMessage({
            tone: "info",
            text: "Backup sharing cancelled",
          });
          return;
        }

        try {
          await downloadPreparedTripWorkbookFile(currentShare.file);
          await refreshBackupMeta();
          setDataMessage({
            tone: "success",
            text: "Sharing was blocked, backup downloaded",
          });
          setPreparedShare(null);
        } catch {
          setDataMessage({
            tone: "error",
            text: "Backup could not be shared or downloaded",
          });
        }
      })
      .finally(() => setIsDataBusy(false));
  };

  const downloadPreparedWorkbook = () => {
    const currentShare = preparedShare;
    if (!currentShare) return;

    setIsDataBusy(true);
    void downloadPreparedTripWorkbookFile(currentShare.file)
      .then(async () => {
        await refreshBackupMeta();
        setDataMessage({
          tone: "success",
          text: `Downloaded ${currentShare.recordCount} data rows`,
        });
        setPreparedShare(null);
      })
      .catch(() => {
        setDataMessage({ tone: "error", text: "Backup download failed" });
      })
      .finally(() => setIsDataBusy(false));
  };

  const protectData = async () => {
    setIsDataBusy(true);
    try {
      const protectionState = await requestPersistentStorage();
      setDataProtectionState(protectionState);
      setDataMessage({
        tone: protectionState === "protected" ? "success" : "info",
        text:
          protectionState === "protected"
            ? "Device storage protection enabled"
            : "This browser did not grant storage protection. Keep backups current.",
      });
    } catch {
      setDataMessage({ tone: "error", text: "Storage protection check failed" });
    } finally {
      setIsDataBusy(false);
    }
  };

  const importWorkbook = async (file: File) => {
    setIsDataBusy(true);
    try {
      const result = await importTripWorkbookFile(file);
      await Promise.all([refreshTrips(), refreshBackupMeta()]);
      setDataMessage({
        tone: result.rejectedCount > 0 ? "error" : "success",
        text:
          result.kind === "workbook"
            ? `Imported ${result.importedCount} data rows, rejected ${result.rejectedCount}`
            : result.kind === "backup"
              ? `Imported ${result.importedCount} backup records, rejected ${result.rejectedCount}`
            : `Imported ${result.importedCount} ${result.kind}, rejected ${result.rejectedCount}`,
      });
    } catch {
      setDataMessage({ tone: "error", text: "Data import failed" });
    } finally {
      setIsDataBusy(false);
    }
  };

  const handleImportFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void importWorkbook(file);
    event.target.value = "";
  };

  const insightPeriodMode: InsightPeriodMode = isInsightAllTime
    ? "all"
    : selectedInsightMonth
      ? "month"
      : "year";
  const selectedPeriod = useMemo(() => {
    if (isInsightAllTime) return "all";
    if (!selectedInsightYear) return "";
    return selectedInsightMonth
      ? `${selectedInsightYear}-${selectedInsightMonth}`
      : selectedInsightYear;
  }, [isInsightAllTime, selectedInsightMonth, selectedInsightYear]);

  const insightData = useMemo(
    () => getInsightData(visibleTrips, insightPeriodMode, selectedPeriod),
    [visibleTrips, insightPeriodMode, selectedPeriod]
  );
  const priorInsightData = useMemo(
    () => getPriorInsightData(visibleTrips, insightPeriodMode, selectedPeriod),
    [visibleTrips, insightPeriodMode, selectedPeriod]
  );
  const insightDeltas = useMemo(
    () => getInsightDeltas(insightData, priorInsightData),
    [insightData, priorInsightData]
  );
  const qualitySignals = useMemo(
    () => getInsightQualitySignals(visibleTrips, insightPeriodMode, selectedPeriod),
    [visibleTrips, insightPeriodMode, selectedPeriod]
  );
  const routeCostVariance = useMemo(
    () => getRouteCostVariance(visibleTrips, insightPeriodMode, selectedPeriod),
    [visibleTrips, insightPeriodMode, selectedPeriod]
  );
  const vehiclePerformance = useMemo(
    () => getVehiclePerformance(insightData),
    [insightData]
  );
  const partyPerformance = useMemo(
    () => getContributionRanking(insightData.parties),
    [insightData]
  );
  const routePerformance = useMemo(
    () => getContributionRanking(insightData.routes),
    [insightData]
  );
  const brokerPerformance = useMemo(
    () => getContributionRanking(insightData.brokers),
    [insightData]
  );

  const exportPartyStatement = async (partyName: string) => {
    setIsDataBusy(true);
    try {
      const exportedCount = await exportPartyStatementToCsvFile(partyName);
      setDataMessage({
        tone: "success",
        text: `Exported ${exportedCount} statement trips`,
      });
    } catch {
      setDataMessage({ tone: "error", text: "Party statement export failed" });
    } finally {
      setIsDataBusy(false);
    }
  };

  const openDataSafety = () => {
    setActiveView("dashboard");
    window.setTimeout(() => {
      const section = document.getElementById("data-safety");
      section?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      section?.focus({ preventScroll: true });
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="orbit-app flex min-h-screen items-center justify-center px-4" data-theme={theme}>
        <div className="orbit-card flex min-w-[220px] flex-col items-center p-5 text-center">
          <BrandMark className="mb-3 h-16 w-16" />
          <p className="text-sm font-semibold text-slate-500">
            Opening local database
          </p>
          <h1 className="mt-1">
            <BrandWordmark className="text-xl" />
          </h1>
        </div>
      </div>
    );
  }

  return (
    <AppShell
      activeView={activeView}
      onViewChange={setActiveView}
      theme={theme}
      onThemeChange={setTheme}
      onNewTrip={openNewTrip}
      onOpenDataSafety={openDataSafety}
      isDataBusy={isDataBusy}
      dataMessage={dataMessage}
    >
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleImportFileChange}
        className="hidden"
        aria-label="Import HaulLedger data file"
      />
      {activeView === "dashboard" ? (
        <DashboardPage
          period={controlPeriod}
          onPeriodChange={setControlPeriod}
          periodMetrics={periodMetrics}
          metrics={metrics}
          activeTripsCount={activeTrips.length}
          exceptionsCount={risks.length}
          queues={operationalQueues}
          dataProtectionState={dataProtectionState}
          backupMeta={backupMeta}
          hasBusinessData={visibleTrips.length > 0}
          isDataBusy={isDataBusy}
          onOpenDesk={() => setActiveView("desk")}
          onProtectData={protectData}
          onBackupData={exportWorkbook}
          onShareBackup={prepareWorkbookShare}
          onRestoreData={() => importInputRef.current?.click()}
        />
      ) : null}

      {activeView === "desk" ? (
        <DeskPage
          risks={risks}
          queues={operationalQueues}
          activeTrips={activeTrips}
          tripsById={tripsById}
          onNewTrip={openNewTrip}
          onStatusChange={updateStatus}
        />
      ) : null}

      {activeView === "trips" ? (
        <TripRegisterPage
          trips={filteredTrips}
          totalTrips={visibleTrips.length}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={tripFilters}
          onFiltersChange={setTripFilters}
          onEdit={openEditTrip}
          onRepeat={openRepeatTrip}
          onDelete={deleteTrip}
          onStatusChange={updateStatus}
          onNewTrip={openNewTrip}
        />
      ) : null}

      {activeView === "cash" ? (
        <MoneyPage trips={visibleTrips} partyOutstanding={partyOutstanding} />
      ) : null}

      {activeView === "parties" ? (
        <PartiesPage
          partyOutstanding={partyOutstanding}
          onExportPartyStatement={exportPartyStatement}
        />
      ) : null}

      {activeView === "analytics" ? (
        <InsightsPage
          periodMode={insightPeriodMode}
          selectedPeriod={selectedPeriod}
          selectedYear={selectedInsightYear}
          selectedMonth={selectedInsightMonth}
          yearOptions={insightYearOptions}
          monthOptions={insightMonthOptions}
          monthsByYear={insightMonthsByYear}
          data={insightData}
          vehiclePerformance={vehiclePerformance}
          partyPerformance={partyPerformance}
          routePerformance={routePerformance}
          brokerPerformance={brokerPerformance}
          deltas={insightDeltas}
          qualitySignals={qualitySignals}
          routeCostVariance={routeCostVariance}
          trend={dashboardTrend}
          onAllTimeChange={setIsInsightAllTime}
          onYearChange={setSelectedInsightYear}
          onMonthChange={setSelectedInsightMonth}
        />
      ) : null}

      <TripFormDrawer
        isOpen={isFormOpen}
        trip={editingTrip}
        initialForm={initialForm}
        payments={editingPayments}
        expenses={editingExpenses}
        suggestions={tripSuggestions}
        onClose={closeForm}
        onSave={upsertTrip}
      />

      {preparedShare ? (
        <BackupShareDialog
          prepared={preparedShare}
          canShare={canShareWorkbookFile(preparedShare.file)}
          isBusy={isDataBusy}
          onClose={() => setPreparedShare(null)}
          onShare={sharePreparedWorkbook}
          onDownload={downloadPreparedWorkbook}
        />
      ) : null}
    </AppShell>
  );
}
